import React, { useCallback } from 'react';
import { PapiUser } from '@wix/da-papi-types';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { UserStream, Props as WidgetProps } from './UserStream';
import { ModuleType } from '../../../../types/modules';
import { inviteToGroup } from '../../../actions/groups';
import WidgetBoxWide, {
  Props as WidgetBoxWideProps,
} from '../../UserWidgets/_partials/WidgetBoxWide';
import getStatLine from '../../../utils/getStatLine';
import EmptyState from '../../EmptyState';
import { Url } from '@wix/da-url';
import UserStreamConfigPopover from './UserStreamConfigPopover';

import s from './UserStreamBox.scss';

interface Props {
  type: ModuleType;
  totalCount?: number;
  isOwner: boolean;
  profileOwnerUser: PapiUser;
  withConfig?: boolean;
}

const UserStreamBoxWide: React.FC<WidgetBoxWideProps & WidgetProps & Props> = ({
  id,
  type,
  subHeader,
  headerExtraElementsOnMobile,
  isHeaderStickyOnMobile,
  totalCount,
  hasMore,
  fetchMore,
  isFetching,
  isEmpty,
  isOwner,
  widgetMenuRenderer,
  widgetMoveUp,
  widgetMoveDown,
  parentRef,
  widgetRemove,
  widgetBoxClassName,
  withConfig,
  section,
  zone,
  ...props
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const dispatchInviteAction = useCallback(
    () => dispatch(inviteToGroup(props.profileOwnerUser)),
    [dispatch, props.profileOwnerUser]
  );
  const onEmptyStateCTAClick = useCallback(() => {
    switch (type) {
      case ModuleType.GROUP_ADMINS:
      case ModuleType.GROUP_MEMBERS:
        return dispatchInviteAction();
      case ModuleType.WATCHERS:
      case ModuleType.ACTIVE_MEMBERS:
      default:
        return window.location.replace(Url.browseDeviantsYouWatchLink());
    }
  }, [type, dispatchInviteAction]);

  return (
    <WidgetBoxWide
      id={id}
      section={section}
      zone={zone}
      moduleId={props.moduleId}
      header={t(`widgets.${type}.header`)}
      count={totalCount}
      subHeader={getStatLine(t, type, totalCount)}
      headerExtraElementsOnMobile={headerExtraElementsOnMobile}
      isHeaderStickyOnMobile={isHeaderStickyOnMobile}
      hasMore={hasMore}
      fetchMore={fetchMore}
      isFetching={isFetching}
      parentRef={parentRef}
      widgetMenuRenderer={
        withConfig
          ? close => (
              <UserStreamConfigPopover
                withManageLink={type === 'watching'}
                moduleId={props.moduleId}
                onClose={close}
              />
            )
          : undefined
      }
      widgetMoveUp={widgetMoveUp}
      widgetMoveDown={widgetMoveDown}
      widgetRemove={widgetRemove}
      className={widgetBoxClassName}
      isEmpty={isEmpty}
      emptyView={
        isOwner ? (
          <EmptyState
            view="small"
            className={s['empty-state']}
            title={t(`widgets.${type}.empty.title.owner`)}
            subtitle={t(`widgets.${type}.empty.subtitle.owner`)}
            withButton={true}
            onButtonClick={onEmptyStateCTAClick}
            buttonLabel={t(`widgets.${type}.empty.CTALabel.owner`)}
          />
        ) : (
          <EmptyState
            view="small"
            className={s['empty-state']}
            title={t(`widgets.${type}.empty.title.visitor`, {
              username: props.profileOwnerUser.username,
            })}
            subtitle={t(`widgets.${type}.empty.subtitle.visitor`, {
              username: props.profileOwnerUser.username,
            })}
          />
        )
      }
    >
      <UserStream {...props} />
    </WidgetBoxWide>
  );
};

export default UserStreamBoxWide;
